/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/router";
import queryString from "query-string";
import { isMobile } from "react-device-detect";
import styled from "styled-components";

import { INVITE_CLAIMED_ERROR } from "@vienna/common";

import { UrbitIcon } from "../Signup/Icons";
import { OrDivider, UrbitButton, IconWrapper } from "../Signup/SignupMethodButton";
import { API } from "src/classes/api";
import { Auth } from "src/classes/user/auth/Auth";
import { AuthBody, AuthHeader, AuthWrapper, EndOfFormText, StyledText } from "src/components/Login/Styles";
import { InviteSVG } from "src/components/Splash/InviteSVG";
import { HighContrastButton } from "src/components/UI/Buttons/ButtonPrimitives";
import { StandardInput } from "src/components/UI/Form";
import { StyledLink } from "src/components/UI/Links";
import { SignupMethod } from "src/lib/auth";
import { AppRoutes } from "src/lib/constants";

export function EnterToken() {
  const router = useRouter();
  const [tokenValue, setTokenValue] = useState("");
  const [submittedToken, setSubmittedToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteClaimed, setInviteClaimed] = useState(false);
  const [codeIncorrect, setCodeIncorrect] = useState(false);
  const [verifiedMobile, setVerifiedMobile] = useState(false);

  // For SSR reasons, we need server and client to agree on this until after mount
  useEffect(() => {
    setVerifiedMobile(isMobile);
  }, []);

  const handleSubmit = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmittedToken(tokenValue);
    Auth.clearLocalStorage();
    return API.auth
      .verifyMnemonic(tokenValue)
      .then(({ token }) => {
        setIsSubmitting(false);
        Auth.writeToken(token);
        router.replace(`${AppRoutes.Onboarding}?${queryString.stringify({ method: SignupMethod.Email })}`);
      })
      .catch((err) => {
        if (err.code === 400 && err.message === INVITE_CLAIMED_ERROR) {
          setInviteClaimed(true);
        } else {
          setIsSubmitting(false);
          setCodeIncorrect(true);
        }
      });
  };

  return (
    <AuthWrapper>
      <AuthBody>
        <AuthHeader>
          <StyledText>
            <Link href={AppRoutes.Home} passHref>
              <StyledLink style={{ color: "#cccccc" }}>VIENNA HYPERTEXT</StyledLink>
            </Link>
            <span style={{ color: "#cccccc" }}> / </span> Register
          </StyledText>
        </AuthHeader>
        <Wrapper>
          <CardWrapper>
            <CardInnerWrapper>
              <InviteSVG />
            </CardInnerWrapper>
          </CardWrapper>
          <form>
            <StandardInput
              alwaysLightMode
              style={{ height: "34px" }}
              type="text"
              name="Verify Invite"
              value={tokenValue}
              placeholder={verifiedMobile ? "Only for desktop, for now" : "Enter token..."}
              id="inviteToken"
              onChange={(e) => setTokenValue(e.target.value)}
              disabled={verifiedMobile}
            />
            {codeIncorrect && (
              <div style={{ margin: "10px 0px 5px 0px", lineHeight: "20px" }}>
                Your token &quot;{submittedToken}&quot; is incorrect or has expired.
              </div>
            )}
            {inviteClaimed && (
              <div style={{ margin: "5px" }}>
                Sorry, token &quot;{submittedToken}&quot; has already been claimed
              </div>
            )}
            <ButtonsWrapper>
              <LDHighContrastButton
                as="input"
                type="submit"
                value="Join"
                id="sendVerifyInvite"
                $disabled={isSubmitting || !tokenValue}
                onClick={(e: React.MouseEvent) => !(isSubmitting || !tokenValue) && handleSubmit(e)}
              />
            </ButtonsWrapper>
          </form>
          <EndOfFormText style={{ fontSize: "14px" }} alwaysLightMode>
            Already have an account?{" "}
            <Link href={AppRoutes.Signin} passHref>
              <RedLink>Sign In</RedLink>
            </Link>
          </EndOfFormText>
          <EndOfFormText style={{ fontSize: "14px" }} alwaysLightMode>
            <div>
              Don&apos;t have a token? You can register for one{" "}
              <Link href={AppRoutes.Waitlist} passHref>
                <RedLink>here</RedLink>
              </Link>
              .
            </div>
          </EndOfFormText>
          <OrDivider />
          <UrbitButton
            onClick={() =>
              router.push(`${AppRoutes.Onboarding}?${queryString.stringify({ method: SignupMethod.Urbit })}`)
            }
          >
            <IconWrapper>
              <UrbitIcon />
            </IconWrapper>
            Signup with Urbit
          </UrbitButton>
        </Wrapper>
      </AuthBody>
    </AuthWrapper>
  );
}

const LDHighContrastButton = styled(HighContrastButton)`
  margin: 11px 0px 0px 0px;
  width: 100%;
  height: 33px;
  font-size: 15px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  border-radius: 0;
  -webkit-appearance: none;
  -webkit-border-radius: 0;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  width: 100%;
`;

const CardWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 25px;
  margin-bottom: 20px;
`;

const CardInnerWrapper = styled.div`
  width: 100%;
  max-width: 339px;
`;

export const RedLink = styled.a`
  color: ${({ theme }) => theme.accent};
`;

const ButtonsWrapper = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
`;
